/**
 * Calculadora de frete da PDP.
 *
 * O /cart/shipping_rates.json da Shopify so cotiza o que esta no carrinho, entao
 * para cotar apenas o produto aberto o fluxo e: guarda o carrinho do cliente ->
 * limpa -> adiciona so este produto -> pede as taxas -> restaura o carrinho.
 * O CEP vira UF pelo ViaCEP, porque o endpoint de taxas exige a provincia.
 */
(function () {
  'use strict';

  var UF_TO_PROVINCE = {
    AC: 'Acre', AL: 'Alagoas', AP: 'Amapa', AM: 'Amazonas',
    BA: 'Bahia', CE: 'Ceara', DF: 'Distrito Federal', ES: 'Espirito Santo',
    GO: 'Goias', MA: 'Maranhao', MT: 'Mato Grosso', MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais', PA: 'Para', PB: 'Paraiba', PR: 'Parana',
    PE: 'Pernambuco', PI: 'Piaui', RJ: 'Rio de Janeiro', RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul', RO: 'Rondonia', RR: 'Roraima', SC: 'Santa Catarina',
    SP: 'Sao Paulo', SE: 'Sergipe', TO: 'Tocantins'
  };

  var VIACEP = 'https://viacep.com.br/ws/';

  function withTimeout(promise, ms) {
    return Promise.race([
      promise,
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('timeout')); }, ms);
      })
    ]);
  }

  /* O bloco so aparece se o ViaCEP responder: sem ele nao ha UF, e sem UF nao ha
     cotacao. Uma segunda tentativa cobre a falha isolada de rede. */
  function viaCepIsOnline() {
    function ping() {
      return withTimeout(
        fetch(VIACEP + '01310100/json/').then(function (r) { return r.json(); }),
        5000
      ).then(function () { return true; });
    }
    return ping().catch(ping).catch(function () { return false; });
  }

  function ShippingCalculator(root) {
    this.root = root;
    this.input = root.querySelector('[data-shipping-zip]');
    this.button = root.querySelector('[data-shipping-submit]');
    this.province = root.querySelector('[data-shipping-province]');
    this.country = root.querySelector('[data-shipping-country]');
    this.error = root.querySelector('[data-shipping-error]');
    this.results = root.querySelector('[data-shipping-results]');
    this.strings = {
      invalidZip: root.dataset.invalidZipMessage,
      notFound: root.dataset.errorMessage,
      freeShipping: root.dataset.freeShippingText,
      noEstimate: root.dataset.noEstimateText,
      estimatePrefix: root.dataset.estimatePrefix
    };
    this.savedItems = [];
    this.busy = false;
  }

  ShippingCalculator.prototype.init = function () {
    if (!this.input || !this.button) return;
    this.input.addEventListener('input', this.mask.bind(this));
    this.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); this.calculate(); }
    }.bind(this));
    this.button.addEventListener('click', function (e) {
      e.preventDefault();
      this.calculate();
    }.bind(this));
  };

  ShippingCalculator.prototype.mask = function (e) {
    var digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    e.target.value = digits.length > 5
      ? digits.slice(0, 5) + '-' + digits.slice(5)
      : digits;
  };

  ShippingCalculator.prototype.showError = function (message) {
    this.results.innerHTML = '';
    this.results.hidden = true;
    this.error.textContent = message;
    this.error.hidden = false;
  };

  ShippingCalculator.prototype.clearError = function () {
    this.error.textContent = '';
    this.error.hidden = true;
  };

  ShippingCalculator.prototype.showLoading = function () {
    this.clearError();
    this.results.hidden = false;
    this.results.innerHTML =
      '<div class="m-product-shipping-calc__loading" role="status" aria-live="polite">' +
      '<span class="m-product-shipping-calc__spinner" aria-hidden="true"></span>' +
      '</div>';
  };

  /* O select de variante e o mesmo input name="id" do formulario de compra, entao
     a cotacao segue a variante que o cliente escolheu. */
  ShippingCalculator.prototype.getVariantId = function () {
    var form = document.querySelector('.m-main-product');
    var select = (form || document).querySelector('[name="id"]');
    if (!select) return null;
    var value = select.value;
    if (!value && select.options && select.options.length) value = select.options[0].value;
    var id = parseInt(value, 10);
    return isNaN(id) ? null : id;
  };

  ShippingCalculator.prototype.getQuantity = function () {
    var field = document.querySelector('.m-main-product .m-quantity__input');
    var qty = field ? parseInt(field.value, 10) : 1;
    return !qty || qty < 1 ? 1 : qty;
  };

  ShippingCalculator.prototype.calculate = function () {
    if (this.busy) return;

    var zip = this.input.value.replace(/\D/g, '');
    if (zip.length !== 8) {
      this.showError(this.strings.invalidZip);
      return;
    }

    var variantId = this.getVariantId();
    if (!variantId) {
      this.showError(this.strings.notFound);
      return;
    }

    this.busy = true;
    this.button.disabled = true;
    this.showLoading();

    var self = this;

    withTimeout(fetch(VIACEP + zip + '/json/').then(function (r) { return r.json(); }), 8000)
      .then(function (data) {
        if (!data || data.erro) throw new Error('cep-not-found');
        self.province.value = UF_TO_PROVINCE[data.uf] || data.uf;
        return self.getRates(variantId);
      })
      .catch(function () {
        self.showError(self.strings.notFound);
      })
      .then(function () {
        self.busy = false;
        self.button.disabled = false;
      });
  };

  ShippingCalculator.prototype.getRates = function (variantId) {
    var self = this;

    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        self.savedItems = cart.items || [];
        return self.clearCart();
      })
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: self.getQuantity() })
        });
      })
      .then(function () {
        var params = new URLSearchParams({
          'shipping_address[country]': self.country.value,
          'shipping_address[province]': self.province.value,
          'shipping_address[zip]': self.input.value
        });
        return fetch('/cart/shipping_rates.json?' + params.toString(), {
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' }
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var rates = data && data.shipping_rates;
        if (!rates || !rates.length) {
          self.showError(self.strings.notFound);
          return;
        }
        self.render(rates);
      })
      .catch(function () {
        self.showError(self.strings.notFound);
      })
      /* O carrinho do cliente volta deu certo ou nao: o clear acima e nosso, nao dele. */
      .then(function () { return self.restoreCart(); });
  };

  ShippingCalculator.prototype.clearCart = function () {
    return fetch('/cart/clear.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    });
  };

  ShippingCalculator.prototype.restoreCart = function () {
    if (!this.savedItems.length) return this.clearCart();

    var items = this.savedItems.map(function (item) {
      var line = { id: item.variant_id, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
      if (item.selling_plan_allocation) line.selling_plan = item.selling_plan_allocation.selling_plan.id;
      return line;
    });
    this.savedItems = [];

    return this.clearCart().then(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });
    });
  };

  ShippingCalculator.prototype.formatPrice = function (price) {
    if (price === null || parseFloat(price) === 0) return this.strings.freeShipping;
    var format = window.MinimogSettings && window.MinimogSettings.money_format;
    return typeof formatMoney === 'function'
      ? formatMoney(parseFloat(price) * 100, format)
      : price;
  };

  /* A data vem como "2026-09-02"; o split evita o new Date(string) tratar como UTC
     e mostrar o dia anterior em fuso negativo. */
  ShippingCalculator.prototype.formatDelivery = function (deliveryDate) {
    if (!deliveryDate) return this.strings.noEstimate;
    var parts = String(deliveryDate).split('-');
    if (parts.length !== 3) return this.strings.noEstimate;
    var date = new Date(parts[0], parts[1] - 1, parts[2]);
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return this.strings.noEstimate;
    return this.strings.estimatePrefix + ' ' + date.toLocaleDateString('pt-BR');
  };

  ShippingCalculator.prototype.render = function (rates) {
    var self = this;
    this.clearError();
    this.results.innerHTML = '';
    this.results.hidden = false;

    rates.forEach(function (rate) {
      var item = document.createElement('div');
      item.className = 'm-product-shipping-calc__item';

      var info = document.createElement('div');
      info.className = 'm-product-shipping-calc__info';

      var name = document.createElement('p');
      name.className = 'm-product-shipping-calc__name';
      name.textContent = rate.name;
      info.appendChild(name);

      var delivery = self.formatDelivery(rate.delivery_date);
      if (delivery) {
        var eta = document.createElement('span');
        eta.className = 'm-product-shipping-calc__delivery';
        eta.textContent = delivery;
        info.appendChild(eta);
      }

      var price = document.createElement('div');
      price.className = 'm-product-shipping-calc__price';
      price.textContent = self.formatPrice(rate.price);

      item.appendChild(info);
      item.appendChild(price);
      self.results.appendChild(item);
    });
  };

  function boot() {
    var roots = document.querySelectorAll('[data-shipping-calculator]');
    if (!roots.length) return;

    viaCepIsOnline().then(function (online) {
      roots.forEach(function (root) {
        if (!online) {
          /* Some o wrapper do bloco, nao so o miolo: a coluna da PDP espaca com
             `gap`, entao um wrapper vazio deixaria o respiro de 25px sobrando. */
          (root.closest('.main-product__block') || root).hidden = true;
          return;
        }
        new ShippingCalculator(root).init();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
